import React, { useEffect, useState } from 'react';
import './ViewInterest.css';
import {
  deleteStoredInterest,
  getStoredInterests,
  updateStoredInterest,
} from '../interestStorage';

const VIEW_INTEREST_SESSION_KEY = 'honouredtech_view_interest_authenticated';
const VIEW_INTEREST_PASSCODE = process.env.REACT_APP_VIEW_INTEREST_PASSCODE || '$ai@@3#';
const ALLOW_LOCAL_FALLBACK =
  process.env.NODE_ENV !== 'production' ||
  process.env.REACT_APP_ENABLE_LOCAL_INTEREST_FALLBACK === 'true';

const ViewInterest = () => {
  const API_BASE =
    process.env.NODE_ENV === 'production'
      ? process.env.REACT_APP_API_BASE || ''
      : '';

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editComments, setEditComments] = useState('');
  const [editInterestLevel, setEditInterestLevel] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [usingLocalFallback, setUsingLocalFallback] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.sessionStorage.getItem(VIEW_INTEREST_SESSION_KEY) === 'true';
  });

  const loadStoredFallback = () => {
    if (!ALLOW_LOCAL_FALLBACK) {
      setList([]);
      setUsingLocalFallback(false);
      setError('Could not load shared interest records. Please ensure the production API is deployed and REACT_APP_API_BASE is configured.');
      return;
    }

    const storedItems = getStoredInterests();
    setList(storedItems);
    setUsingLocalFallback(storedItems.length > 0);

    if (storedItems.length === 0) {
      setError('Failed to load interest details.');
    }
  };

  const loadInterests = () => {
    setLoading(true);
    setError(null);
    setUsingLocalFallback(false);

    fetch(`${API_BASE}/api/interests`)
      .then(async (res) => {
        if (res.ok) {
          const listJson = await res.json();
          setList(Array.isArray(listJson) ? listJson : []);
        } else {
          loadStoredFallback();
        }
      })
      .catch(() => {
        loadStoredFallback();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadInterests();
    }
  }, [isAuthenticated]);

  const handlePasscodeSubmit = (event) => {
    event.preventDefault();

    if (passcode.trim() === VIEW_INTEREST_PASSCODE) {
      window.sessionStorage.setItem(VIEW_INTEREST_SESSION_KEY, 'true');
      setIsAuthenticated(true);
      setPasscodeError('');
      return;
    }

    setPasscodeError('Incorrect passcode. Please try again.');
  };

  const handleLockPage = () => {
    window.sessionStorage.removeItem(VIEW_INTEREST_SESSION_KEY);
    setIsAuthenticated(false);
    setPasscode('');
    setPasscodeError('');
    setEditingItem(null);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    // Start with an empty field; each save appends a new internal comment
    setEditComments('');
    setEditInterestLevel(item.interestLevel || '');
  };

  const handleDelete = (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this record?');
    if (!confirmDelete) return;

    if (String(id).startsWith('local-')) {
      if (!ALLOW_LOCAL_FALLBACK) {
        alert('Local-only records are disabled in production.');
        return;
      }

      const nextItems = deleteStoredInterest(id);
      setList(nextItems);
      setUsingLocalFallback(nextItems.length > 0);
      return;
    }

    fetch(`${API_BASE}/api/interest/${id}`, {
      method: 'DELETE'
    })
      .then((res) => {
        if (res.ok) {
          setList((prev) => prev.filter((item) => item._id !== id));
        } else {
          alert('Failed to delete record.');
        }
      })
      .catch(() => {
        alert('Failed to delete record.');
      });
  };

  if (!isAuthenticated) {
    return (
      <section className="view-interest-page">
        <div className="view-interest-container view-interest-auth-container">
          <h1>View Interest</h1>
          <p className="view-interest-auth-subtitle">
            Enter the passcode to access submitted interest records.
          </p>
          <form className="view-interest-auth-form" onSubmit={handlePasscodeSubmit}>
            <label htmlFor="viewInterestPasscode">Passcode</label>
            <input
              id="viewInterestPasscode"
              type="password"
              value={passcode}
              onChange={(event) => setPasscode(event.target.value)}
              placeholder="Enter passcode"
            />
            {passcodeError && <p className="view-interest-auth-error">{passcodeError}</p>}
            <button type="submit" className="view-interest-auth-button">
              Unlock
            </button>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="view-interest-page">
      <div className="view-interest-container">
        <div className="view-interest-header">
          <h1>Submitted Interest</h1>
          <button
            type="button"
            className="view-interest-lock-button"
            onClick={handleLockPage}
          >
            Exit
          </button>
        </div>
        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p>{error}</p>
        ) : list.length > 0 ? (
          <div className="interest-list">
            <table>
              <thead>
                <tr>
                  <th>Sno</th>
                  <th>Company Name</th>
                  <th>Contact Person</th>
                  <th>Mobile Number</th>
                  <th>My Comments</th>
                  <th>Interest Level</th>
                  <th>Client Comments</th>
                  <th>Submitted At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((item, index) => (
                  <tr
                    key={item._id}
                    onClick={() => openEditModal(item)}
                    style={{ cursor: 'pointer' }}
                    className={
                      item.interestLevel === 'Interested'
                        ? 'interest-row interest-row--interested'
                        : item.interestLevel === 'Partial'
                        ? 'interest-row interest-row--partial'
                        : item.interestLevel === 'Not interested'
                        ? 'interest-row interest-row--not-interested'
                        : 'interest-row'
                    }
                  >
                    <td>{index + 1}</td>
                    <td>{item.companyName}</td>
                    <td>{item.contactPerson}</td>
                    <td>{item.mobileNumber}</td>
                    {/* internal comments added via this screen (stacked) */}
                    <td>
                      {Array.isArray(item.myComments)
                        ? item.myComments.length > 0
                          ? item.myComments.map((c, idx) => (
                              <div key={idx}>{c}</div>
                            ))
                          : '-'
                        : item.myComments || '-'}
                    </td>
                    <td>{item.interestLevel || '-'}</td>
                    {/* client comments come from the original interest form */}
                    <td>{item.comments || '-'}</td>
                    <td>{item.createdAt ? new Date(item.createdAt).toLocaleString() : '-'}</td>
                    <td>
                      <button
                        type="button"
                        className="interest-edit-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(item);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="interest-delete-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item._id);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No interest details found.</p>
        )}
        {editingItem && (
          <div className="interest-modal-overlay">
            <div className="interest-modal">
              <h2>Edit Interest</h2>
              <div className="modal-form-group">
                <label htmlFor="interestLevel">Interest Level</label>
                <select
                  id="interestLevel"
                  value={editInterestLevel}
                  onChange={(e) => setEditInterestLevel(e.target.value)}
                >
                  <option value="">Select interest level</option>
                  <option value="Interested">Interested</option>
                  <option value="Partial">Partial</option>
                  <option value="Not interested">Not interested</option>
                </select>
              </div>
              <div className="modal-form-group">
                <label htmlFor="editComments">My Comments</label>
                <textarea
                  id="editComments"
                  rows="3"
                  value={editComments}
                  onChange={(e) => setEditComments(e.target.value)}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-cancel-button"
                  onClick={() => setEditingItem(null)}
                  disabled={savingEdit}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="modal-save-button"
                  onClick={() => {
                    if (!editingItem) return;

                    if (String(editingItem._id).startsWith('local-')) {
                      if (!ALLOW_LOCAL_FALLBACK) {
                        alert('Local-only records are disabled in production.');
                        return;
                      }

                      const updated = updateStoredInterest(editingItem._id, {
                        myComment: editComments,
                        interestLevel: editInterestLevel,
                      });

                      if (!updated) {
                        alert('Failed to update record.');
                        return;
                      }

                      setList((prev) =>
                        prev.map((item) =>
                          item._id === editingItem._id ? { ...item, ...updated } : item
                        )
                      );
                      setEditingItem(null);
                      return;
                    }

                    setSavingEdit(true);
                    fetch(`${API_BASE}/api/interest/${editingItem._id}`, {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        myComment: editComments,
                        interestLevel: editInterestLevel,
                      }),
                    })
                      .then((res) => {
                        if (res.ok) {
                          return res.json();
                        }
                        throw new Error('Failed to update');
                      })
                      .then((updated) => {
                        setList((prev) =>
                          prev.map((item) =>
                            item._id === updated._id ? { ...item, ...updated } : item
                          )
                        );
                        setEditingItem(null);
                      })
                      .catch(() => {
                        alert('Failed to update record.');
                      })
                      .finally(() => {
                        setSavingEdit(false);
                      });
                  }}
                  disabled={savingEdit}
                >
                  {savingEdit ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ViewInterest;
